import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private toastMessagesSource = new Subject<any>();
  toastMessages$ = this.toastMessagesSource.asObservable();

  /**
   * General method to show a toast message with optional sticky and timeout behavior.
   * @param severity The type of the toast message (e.g., success, error, warn, info).
   * @param summary The summary/title of the toast message.
   * @param detail The detailed description of the toast message.
   * @param sticky (Optional) Whether the toast should be sticky (default: false).
   * @param life (Optional) Time in milliseconds before the toast hides (default: 3000ms).
   */
  private showToast(
    severity: 'success' | 'error' | 'warn' | 'info',
    summary: string,
    detail: string,
    sticky: boolean = false,
    life: number = 3000
  ) {
    const message = { severity, summary, detail, sticky, life };
    this.toastMessagesSource.next(message);
  }

  /**
   * Show a success toast message.
   * @param summary The summary of the toast message.
   * @param detail The detailed description of the toast message.
   * @param sticky (Optional) Whether the toast should be sticky (default: false).
   * @param life (Optional) Time in milliseconds before the toast hides (default: 3000ms).
   */
  showSuccess(summary: string, detail: string, sticky: boolean = false, life: number = 3000) {
    this.showToast('success', summary, detail, sticky, life);
  }

  /**
   * Show an error toast message.
   * @param summary The summary of the toast message.
   * @param detail The detailed description of the toast message.
   * @param sticky (Optional) Whether the toast should be sticky (default: false).
   * @param life (Optional) Time in milliseconds before the toast hides (default: 3000ms).
   */
  showError(summary: string, detail: string, sticky: boolean = false, life: number = 3000) {
    this.showToast('error', summary, detail, sticky, life);
  }

  /**
   * Show a warning toast message.
   * @param summary The summary of the toast message.
   * @param detail The detailed description of the toast message.
   * @param sticky (Optional) Whether the toast should be sticky (default: false).
   * @param life (Optional) Time in milliseconds before the toast hides (default: 3000ms).
   */
  showWarning(summary: string, detail: string, sticky: boolean = false, life: number = 3000) {
    this.showToast('warn', summary, detail, sticky, life);
  }

  /**
   * Show an informational toast message.
   * @param summary The summary of the toast message.
   * @param detail The detailed description of the toast message.
   * @param sticky (Optional) Whether the toast should be sticky (default: false).
   * @param life (Optional) Time in milliseconds before the toast hides (default: 3000ms).
   */
  showInfo(summary: string, detail: string, sticky: boolean = false, life: number = 3000) {
    this.showToast('info', summary, detail, sticky, life);
  }
}
